import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { loginWithNostr, loginWithNsec, getToken, logout, authFetch } from '../auth';
import AuthedImage from '../components/AuthedImage';

function LoginScreen({ onLoggedIn }) {
  const [error, setError] = useState('');
  const [nsec, setNsec] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleExtension() {
    setBusy(true); setError('');
    try {
      await loginWithNostr();
      onLoggedIn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleNsec(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await loginWithNsec(nsec);
      onLoggedIn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 320 }}>
        <h2>Admin login</h2>
        <button disabled={busy} onClick={handleExtension} style={{ width: '100%', marginBottom: 16 }}>
          Login with Nostr extension
        </button>
        <form onSubmit={handleNsec}>
          <input
            type="password"
            placeholder="or paste nsec1..."
            value={nsec}
            onChange={(e) => setNsec(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <button disabled={busy} type="submit" style={{ width: '100%' }}>Login with nsec</button>
        </form>
        {error && <p style={{ color: '#e66' }}>{error}</p>}
      </div>
    </div>
  );
}

function MediaGrid({ media, collections, selected, onToggleSelect, onToggleShared, onDelete, onEditDescription, onSetCollection, onSetTakenAt, reorderable, onMove }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
      {media.map((m, i) => (
        <div
          key={m.id}
          style={{
            border: selected.has(m.id) ? '1px solid #6d5efc' : '1px solid #333',
            borderRadius: 8,
            padding: 8,
            position: 'relative',
          }}
        >
          <input
            type="checkbox"
            checked={selected.has(m.id)}
            onChange={() => onToggleSelect(m.id)}
            style={{ position: 'absolute', top: 12, left: 12, width: 16, height: 16, zIndex: 1 }}
          />
          {reorderable && (
            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: 'flex', gap: 2 }}>
              <button
                type="button"
                disabled={i === 0}
                onClick={() => onMove(m.id, -1)}
                style={{ fontSize: 11, padding: '2px 6px' }}
              >
                ↑
              </button>
              <button
                type="button"
                disabled={i === media.length - 1}
                onClick={() => onMove(m.id, 1)}
                style={{ fontSize: 11, padding: '2px 6px' }}
              >
                ↓
              </button>
            </div>
          )}
          {m.type === 'image' ? (
            <AuthedImage
              src={`/api/admin/media/${m.id}/${m.thumbnail_filename ? 'thumb' : 'file'}`}
              alt=""
              style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4, background: '#1c1d20' }}
            />
          ) : (
            <div style={{ width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1c1d20', borderRadius: 4, fontSize: 32 }}>
              &#9835;
            </div>
          )}
          <p style={{ fontSize: 12, color: '#888', margin: '6px 0 2px' }}>{m.original_filename}</p>
          <textarea
            defaultValue={m.description}
            placeholder="description"
            rows={2}
            style={{ width: '100%', fontSize: 12, marginBottom: 6 }}
            onBlur={(e) => {
              if (e.target.value !== m.description) onEditDescription(m.id, e.target.value);
            }}
          />
          <input
            type="date"
            defaultValue={m.taken_at ? m.taken_at.slice(0, 10) : ''}
            title="Date taken"
            style={{ width: '100%', fontSize: 13, marginBottom: 6 }}
            onBlur={(e) => {
              const value = e.target.value || null;
              if (value !== (m.taken_at ? m.taken_at.slice(0, 10) : null)) onSetTakenAt(m.id, value);
            }}
          />
          <select
            value={m.collection_id || ''}
            onChange={(e) => onSetCollection(m.id, e.target.value || null)}
            style={{ width: '100%', fontSize: 12, marginBottom: 6 }}
          >
            <option value="">No collection</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
            <button
              style={{ fontSize: 12, flex: 1, background: m.shared ? '#1f3d24' : undefined }}
              onClick={() => onToggleShared(m.id, !m.shared)}
            >
              {m.shared ? 'Shared' : 'Not shared'}
            </button>
            <button style={{ fontSize: 12 }} onClick={() => onDelete(m.id)}>Delete</button>
          </div>
        </div>
      ))}
      {media.length === 0 && <p style={{ color: '#666' }}>No media yet.</p>}
    </div>
  );
}

function BulkActions({ count, collections, onShare, onUnshare, onAssignCollection, onClear }) {
  const [collectionId, setCollectionId] = useState('');

  if (!count) return null;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, padding: 8, border: '1px solid #6d5efc', borderRadius: 8 }}>
      <span style={{ fontSize: 13 }}>{count} selected</span>
      <button style={{ fontSize: 12 }} onClick={onShare}>Mark shared</button>
      <button style={{ fontSize: 12 }} onClick={onUnshare}>Mark not shared</button>
      <select value={collectionId} onChange={(e) => setCollectionId(e.target.value)} style={{ fontSize: 12 }}>
        <option value="">Assign to collection…</option>
        {collections.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      <button
        style={{ fontSize: 12 }}
        disabled={!collectionId}
        onClick={() => { onAssignCollection(collectionId); setCollectionId(''); }}
      >
        Apply
      </button>
      <button style={{ fontSize: 12, marginLeft: 'auto' }} onClick={onClear}>Clear selection</button>
    </div>
  );
}

function UploadForm({ collections, onUpload }) {
  const [files, setFiles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [description, setDescription] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const isBatch = files.length > 1;
  const single = files.length === 1 ? files[0] : null;

  function handleFileChange(e) {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFiles(picked);
    setPreviewUrl(picked.length === 1 ? URL.createObjectURL(picked[0]) : null);
  }

  function reset() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFiles([]);
    setPreviewUrl(null);
    setDescription('');
    setCollectionId('');
    setProgress(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!files.length) return;
    setBusy(true);
    try {
      if (isBatch) {
        const failures = [];
        for (let i = 0; i < files.length; i++) {
          setProgress(`Uploading ${i + 1} of ${files.length}…`);
          try {
            await onUpload(files[i], '', collectionId);
          } catch (err) {
            failures.push(`${files[i].name}: ${err.message}`);
          }
        }
        if (failures.length) {
          alert(`${files.length - failures.length} of ${files.length} uploaded.\n\nFailed:\n${failures.join('\n')}`);
        }
      } else {
        await onUpload(single, description, collectionId);
      }
      reset();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <input type="file" accept="image/*,audio/*" multiple onChange={handleFileChange} disabled={busy} />

      {single && (
        <div style={{ marginTop: 12, maxWidth: 320 }}>
          {single.type.startsWith('image/') ? (
            <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 6, display: 'block' }} />
          ) : (
            <audio controls src={previewUrl} style={{ display: 'block', width: '100%' }} />
          )}
          <input
            placeholder="description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ marginTop: 8, width: '100%' }}
          />
          <select
            value={collectionId}
            onChange={(e) => setCollectionId(e.target.value)}
            style={{ marginTop: 8, width: '100%' }}
          >
            <option value="">No collection</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button type="submit" disabled={busy}>{busy ? 'Uploading…' : 'Upload'}</button>
            <button type="button" onClick={reset} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      {isBatch && (
        <div style={{ marginTop: 12, maxWidth: 320 }}>
          <p style={{ fontSize: 13, color: '#888' }}>
            {files.length} files selected. They'll upload with no description — add those afterward below.
          </p>
          <select
            value={collectionId}
            onChange={(e) => setCollectionId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">No collection</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="submit" disabled={busy}>{busy ? 'Uploading…' : `Upload ${files.length} files`}</button>
            <button type="button" onClick={reset} disabled={busy}>Cancel</button>
            {progress && <span style={{ fontSize: 12, color: '#888' }}>{progress}</span>}
          </div>
        </div>
      )}
    </form>
  );
}

function Collections({ collections, counts, onCreate, onRename, onDelete }) {
  const [name, setName] = useState('');

  return (
    <div>
      <form
        onSubmit={(e) => { e.preventDefault(); if (name.trim()) { onCreate(name.trim()); setName(''); } }}
        style={{ display: 'flex', gap: 8, marginBottom: 12 }}
      >
        <input placeholder="new collection name, e.g. Reunion 2026" value={name} onChange={(e) => setName(e.target.value)} style={{ width: 260 }} />
        <button type="submit">Create collection</button>
      </form>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {collections.map((c) => (
            <tr key={c.id} style={{ borderBottom: '1px solid #333' }}>
              <td style={{ padding: '6px 4px' }}>
                <input
                  defaultValue={c.name}
                  style={{ fontSize: 12, width: 220 }}
                  onBlur={(e) => { if (e.target.value.trim() && e.target.value !== c.name) onRename(c.id, e.target.value.trim()); }}
                />
              </td>
              <td style={{ padding: '6px 4px', fontSize: 12, color: '#888' }}>
                {counts[c.id] || 0} file{counts[c.id] === 1 ? '' : 's'}
              </td>
              <td style={{ padding: '6px 4px' }}>
                <button style={{ fontSize: 12 }} onClick={() => onDelete(c.id)}>Delete</button>
              </td>
            </tr>
          ))}
          {collections.length === 0 && (
            <tr><td colSpan={3} style={{ color: '#666', padding: '6px 4px' }}>No collections yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CollectionPicker({ collections, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const label = selectedIds.length === 0
    ? 'All shared items'
    : collections.filter((c) => selectedIds.includes(c.id)).map((c) => c.name).join(', ') || 'All shared items';

  function toggle(id) {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label} ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, background: '#1c1d20', border: '1px solid #444', borderRadius: 6, padding: 8, zIndex: 10, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
            <input type="checkbox" checked={selectedIds.length === 0} onChange={() => onChange([])} /> All shared items
          </label>
          {collections.map((c) => (
            <label key={c.id} style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
              <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggle(c.id)} /> {c.name}
            </label>
          ))}
          <button type="button" onClick={() => setOpen(false)} style={{ fontSize: 12, marginTop: 4 }}>Done</button>
        </div>
      )}
    </div>
  );
}

function ShareLinks({ links, collections, onCreate, onToggleRevoked, onDelete, onChangeScope }) {
  const [label, setLabel] = useState('');
  const [collectionIds, setCollectionIds] = useState([]);

  return (
    <div>
      <form
        onSubmit={(e) => { e.preventDefault(); if (label.trim()) { onCreate(label.trim(), collectionIds); setLabel(''); setCollectionIds([]); } }}
        style={{ display: 'flex', gap: 8, marginBottom: 12 }}
      >
        <input placeholder="label, e.g. Mom" value={label} onChange={(e) => setLabel(e.target.value)} />
        <CollectionPicker collections={collections} selectedIds={collectionIds} onChange={setCollectionIds} />
        <button type="submit">Create link</button>
      </form>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {links.map((l) => {
            const url = `${window.location.origin}/s/${l.token}`;
            return (
              <tr key={l.id} style={{ borderBottom: '1px solid #333' }}>
                <td style={{ padding: '6px 4px' }}>{l.label}</td>
                <td style={{ padding: '6px 4px' }}>
                  <CollectionPicker
                    collections={collections}
                    selectedIds={l.collections.map((c) => c.id)}
                    onChange={(ids) => onChangeScope(l.id, ids)}
                  />
                </td>
                <td style={{ padding: '6px 4px', opacity: l.revoked ? 0.4 : 1 }}>
                  <input readOnly value={url} onClick={(e) => e.target.select()} style={{ width: 260, fontSize: 12 }} />
                </td>
                <td style={{ padding: '6px 4px' }}>
                  <button style={{ fontSize: 12 }} onClick={() => navigator.clipboard.writeText(url)}>Copy</button>
                </td>
                <td style={{ padding: '6px 4px' }}>
                  {!l.revoked && <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>Open ↗</a>}
                </td>
                <td style={{ padding: '6px 4px' }}>
                  <button style={{ fontSize: 12 }} onClick={() => onToggleRevoked(l.id, !l.revoked)}>
                    {l.revoked ? 'Un-revoke' : 'Revoke'}
                  </button>
                </td>
                <td style={{ padding: '6px 4px' }}>
                  <button style={{ fontSize: 12 }} onClick={() => onDelete(l.id)}>Delete</button>
                </td>
              </tr>
            );
          })}
          {links.length === 0 && (
            <tr><td colSpan={7} style={{ color: '#666', padding: '6px 4px' }}>No links yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ViewerPassword({ enabled, onSet, onRemove }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [justSet, setJustSet] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!password.trim()) return;
    setBusy(true);
    try {
      await onSet(password.trim());
      setJustSet(password.trim());
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!window.confirm('Remove password protection? Anyone with a share link will get in without a password.')) return;
    setBusy(true);
    try {
      await onRemove();
      setJustSet('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
        {enabled
          ? 'A password is currently required, on top of the share link itself, to view any shared content.'
          : 'No password is currently required — a valid share link alone is enough to view shared content.'}
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          placeholder={enabled ? 'New password (rotates the current one)' : 'Set a password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ width: 260 }}
        />
        <button type="submit" disabled={busy}>{enabled ? 'Rotate password' : 'Set password'}</button>
        {enabled && <button type="button" onClick={handleRemove} disabled={busy}>Remove protection</button>}
      </form>
      {justSet && (
        <p style={{ fontSize: 13, color: '#8f8' }}>
          Password set to <strong>{justSet}</strong> — send this to everyone now, since it won't be shown again.
          Rotating it later immediately signs out everyone using the old one.
        </p>
      )}
    </div>
  );
}

function Collapsible({ title, storageKey, children }) {
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(`dtmedia-admin:${storageKey}`);
    return saved === null ? true : saved === 'true';
  });

  function toggle() {
    setOpen((o) => {
      const next = !o;
      localStorage.setItem(`dtmedia-admin:${storageKey}`, String(next));
      return next;
    });
  }

  return (
    <section style={{ marginBottom: 32 }}>
      <button
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none',
          padding: 0, marginBottom: open ? 16 : 0, cursor: 'pointer', width: '100%', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 11, color: '#888', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
        <h3 style={{ margin: 0 }}>{title}</h3>
      </button>
      {open && children}
    </section>
  );
}

function Dashboard() {
  const [media, setMedia] = useState([]);
  const [links, setLinks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [mediaFilter, setMediaFilter] = useState('');
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaSort, setMediaSort] = useState('default');
  const [mediaSharedFilter, setMediaSharedFilter] = useState('all');
  const [passwordEnabled, setPasswordEnabled] = useState(false);

  const loadMedia = useCallback(() => {
    authFetch('/api/admin/media').then((r) => r.json()).then(setMedia);
  }, []);
  const loadLinks = useCallback(() => {
    authFetch('/api/admin/share-links').then((r) => r.json()).then(setLinks);
  }, []);
  const loadCollections = useCallback(() => {
    authFetch('/api/admin/collections').then((r) => r.json()).then(setCollections);
  }, []);
  const loadPasswordStatus = useCallback(() => {
    authFetch('/api/admin/settings/viewer-password').then((r) => r.json()).then((d) => setPasswordEnabled(d.enabled));
  }, []);

  useEffect(() => { loadMedia(); loadLinks(); loadCollections(); loadPasswordStatus(); }, [loadMedia, loadLinks, loadCollections, loadPasswordStatus]);

  async function setViewerPassword(password) {
    await authFetch('/api/admin/settings/viewer-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    loadPasswordStatus();
  }

  async function removeViewerPassword() {
    await authFetch('/api/admin/settings/viewer-password', { method: 'DELETE' });
    loadPasswordStatus();
  }

  const collectionCounts = useMemo(() => {
    const counts = {};
    for (const m of media) {
      if (m.collection_id) counts[m.collection_id] = (counts[m.collection_id] || 0) + 1;
    }
    return counts;
  }, [media]);

  const visibleMedia = useMemo(() => {
    if (!mediaFilter) return media;
    return media
      .filter((m) => String(m.collection_id) === String(mediaFilter))
      .sort((a, b) => a.position - b.position || new Date(a.created_at) - new Date(b.created_at));
  }, [media, mediaFilter]);

  const searchedMedia = useMemo(() => {
    const term = mediaSearch.trim().toLowerCase();
    let items = term
      ? visibleMedia.filter(
          (m) => m.original_filename.toLowerCase().includes(term) || m.description.toLowerCase().includes(term)
        )
      : visibleMedia;
    if (mediaSharedFilter === 'shared') items = items.filter((m) => m.shared);
    else if (mediaSharedFilter === 'unshared') items = items.filter((m) => !m.shared);
    if (mediaSort === 'taken') {
      items = [...items].sort(
        (a, b) => new Date(b.taken_at || b.created_at) - new Date(a.taken_at || a.created_at)
      );
    }
    return items;
  }, [visibleMedia, mediaSearch, mediaSharedFilter, mediaSort]);

  function moveMedia(id, direction) {
    const ids = visibleMedia.map((m) => m.id);
    const i = ids.indexOf(id);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    authFetch(`/api/admin/collections/${mediaFilter}/order`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ media_ids: ids }),
    }).then(loadMedia);
  }

  async function handleUpload(file, description, collectionId) {
    const form = new FormData();
    form.append('file', file);
    form.append('description', description);
    const resp = await authFetch('/api/admin/media', { method: 'POST', body: form });
    let created;
    try {
      created = await resp.json();
    } catch {
      // Non-JSON body (e.g. an nginx error page for an oversized file, or a
      // dropped connection) rather than a proper API error response.
      throw new Error(`upload failed (HTTP ${resp.status}) — file may be too large or the connection was interrupted`);
    }
    if (!resp.ok) throw new Error(created.error || 'upload failed');
    if (collectionId) {
      await authFetch(`/api/admin/media/${created.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collection_id: Number(collectionId) }),
      });
    }
    loadMedia();
  }

  function toggleShared(id, shared) {
    authFetch(`/api/admin/media/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shared }),
    }).then(loadMedia);
  }

  function editDescription(id, description) {
    authFetch(`/api/admin/media/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description }),
    }).then(loadMedia);
  }

  function setTakenAt(id, taken_at) {
    authFetch(`/api/admin/media/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taken_at }),
    }).then(loadMedia);
  }

  function setMediaCollection(id, collection_id) {
    authFetch(`/api/admin/media/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ collection_id: collection_id ? Number(collection_id) : null }),
    }).then(loadMedia);
  }

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function bulkPatch(body) {
    await Promise.all(
      Array.from(selected).map((id) =>
        authFetch(`/api/admin/media/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      )
    );
    loadMedia();
    clearSelection();
  }

  function bulkShare() { bulkPatch({ shared: true }); }
  function bulkUnshare() { bulkPatch({ shared: false }); }
  function bulkAssignCollection(collectionId) { bulkPatch({ collection_id: Number(collectionId) }); }

  function deleteMedia(id) {
    if (!window.confirm('Delete this file permanently?')) return;
    authFetch(`/api/admin/media/${id}`, { method: 'DELETE' }).then(loadMedia);
  }

  function createCollection(name) {
    authFetch('/api/admin/collections', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(loadCollections);
  }

  function renameCollection(id, name) {
    authFetch(`/api/admin/collections/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(loadCollections);
  }

  function deleteCollection(id) {
    if (!window.confirm('Delete this collection? Files stay, just become uncategorized.')) return;
    authFetch(`/api/admin/collections/${id}`, { method: 'DELETE' }).then(() => { loadCollections(); loadMedia(); });
  }

  function createLink(label, collectionIds) {
    authFetch('/api/admin/share-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, collection_ids: collectionIds.map(Number) }),
    }).then(loadLinks);
  }

  function toggleRevoked(id, revoked) {
    authFetch(`/api/admin/share-links/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ revoked }),
    }).then(loadLinks);
  }

  function changeShareLinkScope(id, collectionIds) {
    authFetch(`/api/admin/share-links/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ collection_ids: collectionIds.map(Number) }),
    }).then(loadLinks);
  }

  function deleteLink(id) {
    if (!window.confirm('Delete this link permanently?')) return;
    authFetch(`/api/admin/share-links/${id}`, { method: 'DELETE' }).then(loadLinks);
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>Admin</h2>
        <button onClick={() => { logout(); window.location.reload(); }}>Log out</button>
      </div>

      <Collapsible title="Collections" storageKey="collections">
        <Collections
          collections={collections}
          counts={collectionCounts}
          onCreate={createCollection}
          onRename={renameCollection}
          onDelete={deleteCollection}
        />
      </Collapsible>

      <Collapsible title="Files" storageKey="files">
        <h4 style={{ margin: '0 0 8px' }}>Upload</h4>
        <UploadForm collections={collections} onUpload={handleUpload} />

        <h4 style={{ margin: '24px 0 8px' }}>Media ({media.length})</h4>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#888' }}>Filter:</span>
          <select value={mediaFilter} onChange={(e) => setMediaFilter(e.target.value)} style={{ fontSize: 13 }}>
            <option value="">All ({media.length})</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({collectionCounts[c.id] || 0})</option>
            ))}
          </select>
          <input
            placeholder="Search filename or caption…"
            value={mediaSearch}
            onChange={(e) => setMediaSearch(e.target.value)}
            style={{ fontSize: 13, width: 220 }}
          />
          <select value={mediaSharedFilter} onChange={(e) => setMediaSharedFilter(e.target.value)} style={{ fontSize: 13 }}>
            <option value="all">Shared + not shared</option>
            <option value="shared">Shared only</option>
            <option value="unshared">Not shared only</option>
          </select>
          <span style={{ fontSize: 13, color: '#888' }}>Sort:</span>
          <select value={mediaSort} onChange={(e) => setMediaSort(e.target.value)} style={{ fontSize: 13 }}>
            <option value="default">Upload order</option>
            <option value="taken">Date taken</option>
          </select>
          {mediaFilter && !mediaSearch && mediaSharedFilter === 'all' && mediaSort === 'default' && (
            <span style={{ fontSize: 12, color: '#888' }}>Use ↑ ↓ to set the order shown in the viewer.</span>
          )}
        </div>
        <BulkActions
          count={selected.size}
          collections={collections}
          onShare={bulkShare}
          onUnshare={bulkUnshare}
          onAssignCollection={bulkAssignCollection}
          onClear={clearSelection}
        />
        <MediaGrid
          media={searchedMedia}
          collections={collections}
          selected={selected}
          onToggleSelect={toggleSelect}
          onToggleShared={toggleShared}
          onDelete={deleteMedia}
          onEditDescription={editDescription}
          onSetTakenAt={setTakenAt}
          onSetCollection={setMediaCollection}
          reorderable={!!mediaFilter && !mediaSearch && mediaSharedFilter === 'all' && mediaSort === 'default'}
          onMove={moveMedia}
        />
      </Collapsible>

      <Collapsible title="Sharing" storageKey="sharing">
        <h4 style={{ margin: '0 0 8px' }}>Share links</h4>
        <ShareLinks
          links={links}
          collections={collections}
          onCreate={createLink}
          onToggleRevoked={toggleRevoked}
          onDelete={deleteLink}
          onChangeScope={changeShareLinkScope}
        />

        <h4 style={{ margin: '24px 0 8px' }}>Viewer password</h4>
        <ViewerPassword enabled={passwordEnabled} onSet={setViewerPassword} onRemove={removeViewerPassword} />
      </Collapsible>
    </div>
  );
}

export default function Admin() {
  const [loggedIn, setLoggedIn] = useState(!!getToken());

  if (!loggedIn) return <LoginScreen onLoggedIn={() => setLoggedIn(true)} />;
  return <Dashboard />;
}
